import styled from '@emotion/styled';
import React, { useCallback, useEffect, useState } from 'react';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { Color } from '@diritto/common/style/color';
import { range, shuffle } from '@diritto/common/utils/array';
import { TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { SelectDefaultCoverImageList } from 'app-components/contents/editCoverImage/SelectDefaultCoverImageList';
import { TextCloseButton } from 'ui-components/navigations/HeaderButtons';
import { configure } from 'utils/configure';
import { useTrackingState } from 'contexts/TrackingProvider';
import { RedoIcon } from 'icons/RedoIcon';

interface SelectDefaultCoverImageProps {
  className?: string;
  onSelected: (imageFilePath: string) => void;
  onClose: () => void;
}

export const SelectDefaultCoverImage = ({ className, onSelected, onClose }: SelectDefaultCoverImageProps) => {
  const { tracker } = useTrackingState();
  const [defaultImagePaths, setDefaultImagePaths] = useState<string[]>([]);

  const updateDefaultImagePaths = useCallback(() => {
    const numbers = shuffle(range(46)).slice(0, 36);
    numbers.map(index => tracker.track(TrackingEventType.ShowedContentCoverEditorImage, { index: index + 1 }));

    setDefaultImagePaths(
      numbers.map(
        index =>
          `${configure.staticPrefix}/images/covers/season06/v6-${(index + 1).toLocaleString('en-US', {
            minimumIntegerDigits: 2,
            useGrouping: false,
          })}.jpg`,
      ),
    );
  }, [tracker]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    updateDefaultImagePaths();
  }, [updateDefaultImagePaths]);

  return (
    <StyledContainer className={className}>
      <StyledWrapper>
        <StyledTitleWrapper>
          <TextCloseButton onClick={handleClose} />
          <StyledTitle>표지 선택</StyledTitle>
          <StyledRedo onClick={updateDefaultImagePaths}>
            <RedoIcon />
          </StyledRedo>
        </StyledTitleWrapper>
        <StyledImageList imageFilePaths={defaultImagePaths} onSelected={onSelected} />
      </StyledWrapper>
    </StyledContainer>
  );
};

const StyledRedo = styled.div`
  display: flex;
  text-align: right;
  cursor: pointer;
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const StyledTitle = styled.p`
  font-weight: ${FontWeight.Bold};
  ${Typography.XSmall};
  flex: 8;
  text-align: center;
`;

const StyledTitleWrapper = styled.div`
  padding: 16px;
  width: 100%;
  height: 56px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${Color.Gray300};
`;

const StyledWrapper = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  flex-grow: 1;
`;

const StyledImageList = styled(SelectDefaultCoverImageList)`
  width: 100%;
  padding: 16px;
`;
